import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import {
  listCitizenRequestHistory,
} from '../../utils/citizenRequestHistory';
import { setDiscoverySessionId } from '../../utils/discoverySession';

function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/**
 * Last 5 advisor requests for this browser. Restore via ?session= + city.
 */
export default function CitizenRequestHistory({
  packageId = null,
  variant = 'menu', // menu | list
  onRestored = null,
  refreshToken = 0,
}) {
  const [anchor, setAnchor] = useState(null);
  const [rows, setRows] = useState([]);

  const refresh = useCallback(() => {
    setRows(listCitizenRequestHistory({ packageId, limit: 5 }));
  }, [packageId]);

  useEffect(() => {
    refresh();
  }, [refresh, refreshToken]);

  const restore = (row) => {
    if (!row?.sessionId) return;
    setDiscoverySessionId(row.sessionId);
    const params = new URLSearchParams();
    if (row.packageId || packageId) params.set('city', row.packageId || packageId);
    params.set('session', row.sessionId);
    const path = `${window.location.pathname}?${params.toString()}`;
    setAnchor(null);
    onRestored?.(row);
    window.location.assign(path);
  };

  if (variant === 'list') {
    if (!rows.length) return null;
    return (
      <Box data-testid="citizen-request-history-list" sx={{ mb: 0.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.75 }}>
          Recent requests
        </Typography>
        {rows.map((row) => (
          <Button
            key={row.sessionId}
            fullWidth
            size="small"
            onClick={() => restore(row)}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              textAlign: 'left',
              mb: 0.5,
              px: 1,
              py: 0.75,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
            }}
            data-testid={`citizen-history-item-${row.sessionId}`}
          >
            <Box sx={{ minWidth: 0, width: '100%' }}>
              <Typography variant="body2" fontWeight={650} noWrap>
                {row.primaryTrack || row.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {[row.address, relativeTime(row.updatedAt)].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          </Button>
        ))}
      </Box>
    );
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<HistoryIcon />}
        onClick={(e) => {
          refresh();
          setAnchor(e.currentTarget);
        }}
        disabled={!rows.length}
        sx={{
          textTransform: 'none',
          minHeight: 30,
          px: 1.1,
          borderColor: 'divider',
          color: 'text.secondary',
        }}
        data-testid="citizen-request-history"
      >
        Recent
        {rows.length ? ` (${rows.length})` : ''}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {!rows.length && (
          <MenuItem disabled>
            <ListItemText primary="No recent requests" />
          </MenuItem>
        )}
        {rows.map((row) => (
          <MenuItem
            key={row.sessionId}
            onClick={() => restore(row)}
            data-testid={`citizen-history-menu-${row.sessionId}`}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <PlaceOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={row.primaryTrack || row.title}
              secondary={[row.address, relativeTime(row.updatedAt)].filter(Boolean).join(' · ')}
              primaryTypographyProps={{ noWrap: true, style: { maxWidth: 240 } }}
              secondaryTypographyProps={{ noWrap: true, style: { maxWidth: 240 } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
